import { Controller } from "@hotwired/stimulus"
import { formatDisplayTime } from "../shared/date_picker_helpers"

export default class extends Controller {
  static targets = [
    "form",
    "bar",
    "panel",
    "sheet",
    "backdrop",
    "compactTrigger",
    "compactLabel",
    "locationInput",
    "latInput",
    "lngInput",
    "dateInput",
    "dateLabel",
    "dateClear",
    "cityDropdown",
    "dateDropdown",
    "dateQuick",
    "dateCalendar",
    "timeInput",
    "timeLabel",
    "timeClear",
    "timeDropdown",
    "timeQuick",
    "timeGrid",
    "suggestions"
  ]

  static values = {
    cities: Array,
    anyDate: String,
    today: String,
    tomorrow: String,
    pickDate: String,
    anyTime: String,
    pickTime: String,
    placeholder: String
  }

  connect() {
    this.blurTimeout = null
    this.highlightIndex = -1
    this.modalOpen = false
    this.panelPortaled = false
    this.historyPushed = false
    this.ignorePopState = false
    this.calendarView = this.startOfMonth(new Date())
    this.cacheElements()
    this.bindPanelEvents()
    this.setAnyDate()
    this.setAnyTime()
    this.updateCompactLabel()
    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundDocumentClick = this.handleDocumentClick.bind(this)
    this.boundPopState = this.handlePopState.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
    document.addEventListener("click", this.boundDocumentClick)
    window.addEventListener("popstate", this.boundPopState)
  }

  disconnect() {
    clearTimeout(this.blurTimeout)
    document.removeEventListener("keydown", this.boundKeydown)
    document.removeEventListener("click", this.boundDocumentClick)
    window.removeEventListener("popstate", this.boundPopState)
    this.unbindPanelEvents()
    if (this.modalOpen && this.historyPushed) {
      this.ignorePopState = true
      this.historyPushed = false
      history.back()
    }
    this.restorePanel()
    this.setChromeHidden(false)
    this.unlockScroll()
  }

  cacheElements() {
    this.el = {
      panel: this.panelTarget,
      sheet: this.sheetTarget,
      backdrop: this.hasBackdropTarget ? this.backdropTarget : null,
      form: this.formTarget,
      bar: this.barTarget,
      compactTrigger: this.hasCompactTriggerTarget ? this.compactTriggerTarget : null,
      compactLabel: this.hasCompactLabelTarget ? this.compactLabelTarget : null,
      locationInput: this.locationInputTarget,
      latInput: this.hasLatInputTarget ? this.latInputTarget : null,
      lngInput: this.hasLngInputTarget ? this.lngInputTarget : null,
      dateInput: this.dateInputTarget,
      dateLabel: this.dateLabelTarget,
      dateClear: this.hasDateClearTarget ? this.dateClearTarget : null,
      dateRow: this.panelTarget.querySelector(".hero-search__date-row"),
      dateSegment: this.panelTarget.querySelector(".hero-search__segment--date"),
      locationSegment: this.panelTarget.querySelector(".hero-search__segment--location"),
      sheetClose: this.panelTarget.querySelector(".hero-search__sheet-close"),
      cityDropdown: this.cityDropdownTarget,
      dateDropdown: this.dateDropdownTarget,
      dateQuick: this.dateQuickTarget,
      dateCalendar: this.dateCalendarTarget,
      timeSegment: this.panelTarget.querySelector(".hero-search__segment--time"),
      timeInput: this.hasTimeInputTarget ? this.timeInputTarget : null,
      timeLabel: this.hasTimeLabelTarget ? this.timeLabelTarget : null,
      timeClear: this.hasTimeClearTarget ? this.timeClearTarget : null,
      timeDropdown: this.hasTimeDropdownTarget ? this.timeDropdownTarget : null,
      timeQuick: this.hasTimeQuickTarget ? this.timeQuickTarget : null,
      timeGrid: this.hasTimeGridTarget ? this.timeGridTarget : null,
      suggestions: this.suggestionsTarget
    }
  }

  bindPanelEvents() {
    this.boundCloseModal = (event) => {
      event.preventDefault()
      this.closeModal()
    }
    this.boundOpenDatePicker = (event) => {
      if (event.target.closest(".hero-search__date-clear")) return
      if (event.target.closest(".hero-search__dropdown--date")) return
      event.preventDefault()
      this.openDatePicker(event)
    }
    this.boundOpenTimePicker = (event) => {
      if (event.target.closest(".hero-search__date-clear")) return
      if (event.target.closest(".hero-search__dropdown--time")) return
      event.preventDefault()
      this.openTimePicker(event)
    }
    this.boundClearDate = (event) => this.clearDate(event)
    this.boundClearTime = (event) => this.clearTime(event)
    this.boundFilterCities = () => this.filterCities()
    this.boundUpdateCompactLabel = () => this.updateCompactLabel()
    this.boundOpenSuggestions = () => this.openSuggestions()
    this.boundCloseSuggestionsDelayed = () => this.closeSuggestionsDelayed()
    this.boundKeydownInput = (event) => this.keydown(event)
    this.boundSubmit = () => {
      this.submit()
    }

    this.el.backdrop?.addEventListener("click", this.boundCloseModal)
    this.el.sheetClose?.addEventListener("click", this.boundCloseModal)
    this.el.dateSegment?.addEventListener("click", this.boundOpenDatePicker)
    this.el.timeSegment?.addEventListener("click", this.boundOpenTimePicker)
    this.el.dateClear?.addEventListener("click", this.boundClearDate)
    this.el.timeClear?.addEventListener("click", this.boundClearTime)
    this.el.locationInput?.addEventListener("input", this.boundFilterCities)
    this.el.locationInput?.addEventListener("input", this.boundUpdateCompactLabel)
    this.el.locationInput?.addEventListener("focus", this.boundOpenSuggestions)
    this.el.locationInput?.addEventListener("blur", this.boundCloseSuggestionsDelayed)
    this.el.locationInput?.addEventListener("keydown", this.boundKeydownInput)
    this.el.form?.addEventListener("submit", this.boundSubmit)
  }

  unbindPanelEvents() {
    this.el.backdrop?.removeEventListener("click", this.boundCloseModal)
    this.el.sheetClose?.removeEventListener("click", this.boundCloseModal)
    this.el.dateSegment?.removeEventListener("click", this.boundOpenDatePicker)
    this.el.timeSegment?.removeEventListener("click", this.boundOpenTimePicker)
    this.el.dateClear?.removeEventListener("click", this.boundClearDate)
    this.el.timeClear?.removeEventListener("click", this.boundClearTime)
    this.el.locationInput?.removeEventListener("input", this.boundFilterCities)
    this.el.locationInput?.removeEventListener("input", this.boundUpdateCompactLabel)
    this.el.locationInput?.removeEventListener("focus", this.boundOpenSuggestions)
    this.el.locationInput?.removeEventListener("blur", this.boundCloseSuggestionsDelayed)
    this.el.locationInput?.removeEventListener("keydown", this.boundKeydownInput)
    this.el.form?.removeEventListener("submit", this.boundSubmit)
  }

  get isMobile() {
    return window.matchMedia("(max-width: 1023px)").matches
  }

  get isDesktop() {
    return !this.isMobile && !this.modalOpen
  }

  getSpecializationPicker() {
    const element = this.element.querySelector('[data-controller~="specialization-picker"]')
    if (!element) return null
    return this.application.getControllerForElementAndIdentifier(element, "specialization-picker")
  }

  portalPanel() {
    if (this.el.panel.parentElement !== document.body) {
      document.body.appendChild(this.el.panel)
      this.panelPortaled = true
    }
  }

  restorePanel() {
    if (this.panelPortaled && this.el.panel.parentElement === document.body) {
      this.element.appendChild(this.el.panel)
      this.panelPortaled = false
    }
  }

  setChromeHidden(hidden) {
    document.body.classList.toggle("hero-search-open", hidden)
  }

  openModal(event) {
    event?.preventDefault()
    if (!this.isMobile) return

    this.portalPanel()
    this.modalOpen = true
    this.el.panel.classList.add("is-open")
    if (this.el.backdrop) this.el.backdrop.classList.add("is-visible")
    if (this.el.compactTrigger) {
      this.el.compactTrigger.setAttribute("aria-expanded", "true")
    }
    this.setChromeHidden(true)
    this.lockScroll()

    if (!this.historyPushed) {
      history.pushState({ heroSearchModal: true }, "")
      this.historyPushed = true
    }

    requestAnimationFrame(() => {
      this.el.sheet.classList.add("is-visible")
    })
  }

  closeModal() {
    if (!this.isMobile || !this.modalOpen) return

    if (this.historyPushed) {
      this.historyPushed = false
      this.ignorePopState = true
      history.back()
    }

    this.finishCloseModal()
  }

  finishCloseModal() {
    if (!this.modalOpen) return

    this.modalOpen = false
    this.el.sheet.classList.remove("is-visible")
    if (this.el.backdrop) this.el.backdrop.classList.remove("is-visible")
    if (this.el.compactTrigger) {
      this.el.compactTrigger.setAttribute("aria-expanded", "false")
    }
    this.closeAllDropdowns()
    this.setChromeHidden(false)

    window.setTimeout(() => {
      this.el.panel.classList.remove("is-open")
      this.restorePanel()
      this.unlockScroll()
    }, 260)
  }

  handlePopState() {
    if (this.ignorePopState) {
      this.ignorePopState = false
      return
    }

    if (!this.modalOpen) return

    this.historyPushed = false
    this.finishCloseModal()
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      if (this.modalOpen) {
        if (!this.el.dateDropdown.classList.contains("hidden")) {
          event.preventDefault()
          this.closeDateDropdown()
          return
        }
        if (this.el.timeDropdown && !this.el.timeDropdown.classList.contains("hidden")) {
          event.preventDefault()
          this.closeTimeDropdown()
          return
        }
        const specializationPicker = this.getSpecializationPicker()
        if (specializationPicker?.isOpen()) {
          event.preventDefault()
          specializationPicker.closeDropdown()
          return
        }
        event.preventDefault()
        this.closeModal()
      } else if (this.isDesktop) {
        this.closeAllDropdowns()
      } else if (!this.el.cityDropdown.classList.contains("hidden")) {
        this.closeSuggestions()
      }
    }
  }

  handleDocumentClick(event) {
    if (!this.isDesktop) return
    if (this.element.contains(event.target)) return
    this.closeAllDropdowns()
  }

  lockScroll() {
    document.documentElement.classList.add("overflow-hidden")
    document.body.classList.add("overflow-hidden")
  }

  unlockScroll() {
    document.documentElement.classList.remove("overflow-hidden")
    document.body.classList.remove("overflow-hidden")
  }

  filterCities() {
    const query = this.el.locationInput.value.trim().toLowerCase()

    if (query.length === 0 && this.isMobile) {
      this.closeSuggestions()
      return
    }

    const matches = query.length === 0
      ? this.citiesValue.slice(0, 6)
      : this.citiesValue.filter((city) => {
          const name = city.name.toLowerCase()
          const country = (city.country || "").toLowerCase()
          return name.includes(query) || country.includes(query)
        }).slice(0, 6)

    this.renderSuggestions(matches)
  }

  renderSuggestions(cities) {
    this.highlightIndex = -1
    this.el.suggestions.innerHTML = ""

    if (cities.length === 0) {
      this.closeSuggestions()
      return
    }

    cities.forEach((city, index) => {
      const item = document.createElement("li")
      item.className = "hero-search__suggestion"
      item.dataset.index = index
      item.dataset.name = city.name
      item.dataset.lat = city.lat
      item.dataset.lng = city.lng
      item.setAttribute("role", "option")
      item.innerHTML = `
        <span class="hero-search__suggestion-icon" aria-hidden="true">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/></svg>
        </span>
        <span class="hero-search__suggestion-text">
          <span class="hero-search__suggestion-city">${city.name}</span>
          ${city.country ? `<span class="hero-search__suggestion-country">${city.country}</span>` : ""}
        </span>
      `
      item.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.applyCity(city)
      })
      this.el.suggestions.appendChild(item)
    })

    this.closeDateDropdown()
    if (this.el.timeDropdown) this.closeTimeDropdown()
    this.getSpecializationPicker()?.closeDropdown()
    this.el.cityDropdown.classList.remove("hidden")
    this.el.locationSegment?.classList.add("hero-search__segment--dropdown-open")
    if (this.isMobile) this.el.bar.classList.add("hero-search__bar--open")
  }

  closeSuggestions() {
    this.el.cityDropdown.classList.add("hidden")
    this.el.suggestions.innerHTML = ""
    this.el.locationSegment?.classList.remove("hero-search__segment--dropdown-open")
    if (this.isMobile) this.el.bar.classList.remove("hero-search__bar--open")
  }

  openSuggestions() {
    if (!this.isMobile || this.modalOpen) {
      this.filterCities()
    }
  }

  closeSuggestionsDelayed() {
    if (this.isDesktop) return
    this.blurTimeout = setTimeout(() => this.closeSuggestions(), 150)
  }

  keydown(event) {
    if (this.el.cityDropdown.classList.contains("hidden")) return

    const items = Array.from(this.el.suggestions.querySelectorAll(".hero-search__suggestion"))
    if (items.length === 0) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.highlightIndex = Math.min(this.highlightIndex + 1, items.length - 1)
      this.updateHighlight(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.highlightIndex = Math.max(this.highlightIndex - 1, 0)
      this.updateHighlight(items)
    } else if (event.key === "Enter" && this.highlightIndex >= 0) {
      event.preventDefault()
      items[this.highlightIndex].dispatchEvent(new MouseEvent("mousedown"))
    } else if (event.key === "Escape") {
      if (this.modalOpen) {
        this.closeModal()
      } else {
        this.closeSuggestions()
      }
    }
  }

  updateHighlight(items) {
    items.forEach((item, index) => {
      item.classList.toggle("hero-search__suggestion--active", index === this.highlightIndex)
    })
  }

  applyCity(city) {
    this.el.locationInput.value = city.name
    if (this.el.latInput) this.el.latInput.value = city.lat
    if (this.el.lngInput) this.el.lngInput.value = city.lng
    this.closeSuggestions()
    this.updateCompactLabel()
  }

  openDatePicker(event) {
    if (event?.target?.closest(".hero-search__date-clear")) return
    if (event?.target?.closest(".hero-search__dropdown--date")) return

    this.closeSuggestions()
    this.closeTimeDropdown()
    this.getSpecializationPicker()?.closeDropdown()

    const isOpen = !this.el.dateDropdown.classList.contains("hidden")
    if (isOpen) {
      this.closeDateDropdown()
    } else {
      this.openDateDropdown()
    }
  }

  openTimePicker(event) {
    if (event?.target?.closest(".hero-search__date-clear")) return
    if (event?.target?.closest(".hero-search__dropdown--time")) return

    this.closeSuggestions()
    this.closeDateDropdown()
    this.getSpecializationPicker()?.closeDropdown()

    const isOpen = !this.el.timeDropdown.classList.contains("hidden")
    if (isOpen) {
      this.closeTimeDropdown()
    } else {
      this.openTimeDropdown()
    }
  }

  openDateDropdown() {
    this.closeSuggestions()
    this.el.dateDropdown.classList.remove("hidden")
    this.el.dateSegment?.classList.add("hero-search__segment--dropdown-open")
    this.renderDateDropdown()
  }

  closeDateDropdown() {
    this.el.dateDropdown.classList.add("hidden")
    this.el.dateSegment?.classList.remove("hero-search__segment--dropdown-open")
  }

  openTimeDropdown() {
    if (!this.el.timeDropdown) return
    this.closeSuggestions()
    this.el.timeDropdown.classList.remove("hidden")
    this.el.timeSegment?.classList.add("hero-search__segment--dropdown-open")
    this.renderTimeDropdown()
  }

  closeTimeDropdown() {
    if (!this.el.timeDropdown) return
    this.el.timeDropdown.classList.add("hidden")
    this.el.timeSegment?.classList.remove("hero-search__segment--dropdown-open")
  }

  closeAllDropdowns() {
    this.closeSuggestions()
    this.closeDateDropdown()
    this.closeTimeDropdown()
    this.getSpecializationPicker()?.closeDropdown()
  }

  renderDateDropdown() {
    this.renderDateQuickPicks()
    this.renderCalendar()
  }

  renderDateQuickPicks() {
    const selected = this.el.dateInput.value
    const todayIso = this.toIsoDate(new Date())
    const tomorrowIso = this.toIsoDate(this.addDays(new Date(), 1))

    const options = [
      { label: this.anyDateValue, value: null },
      { label: this.todayValue, value: todayIso },
      { label: this.tomorrowValue, value: tomorrowIso }
    ]

    this.el.dateQuick.innerHTML = options.map((option) => {
      const active = option.value === null ? !selected : selected === option.value
      return `<button type="button" class="hero-search__date-quick-btn${active ? " hero-search__date-quick-btn--active" : ""}" data-date-value="${option.value ?? ""}">${option.label}</button>`
    }).join("")

    this.el.dateQuick.querySelectorAll(".hero-search__date-quick-btn").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault()
        event.stopPropagation()
        const value = button.dataset.dateValue
        if (value) {
          this.selectDate(value)
        } else {
          this.setAnyDate()
          this.closeDateDropdown()
          this.updateCompactLabel()
        }
      })
    })
  }

  renderCalendar() {
    const year = this.calendarView.getFullYear()
    const month = this.calendarView.getMonth()
    const monthLabel = this.calendarView.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    const weekdays = this.weekdayLabels()
    const days = this.calendarDays(year, month)
    const selected = this.el.dateInput.value
    const todayIso = this.toIsoDate(new Date())
    const minDate = this.toIsoDate(new Date())

    this.el.dateCalendar.innerHTML = `
      <div class="hero-search__calendar-nav">
        <button type="button" class="hero-search__calendar-nav-btn" data-calendar-action="prev" aria-label="Previous month">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7"/></svg>
        </button>
        <span class="hero-search__calendar-month">${monthLabel}</span>
        <button type="button" class="hero-search__calendar-nav-btn" data-calendar-action="next" aria-label="Next month">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/></svg>
        </button>
      </div>
      <div class="hero-search__calendar-weekdays">
        ${weekdays.map((label) => `<span class="hero-search__calendar-weekday">${label}</span>`).join("")}
      </div>
      <div class="hero-search__calendar-grid">
        ${days.map((day) => {
          if (!day) return `<span class="hero-search__calendar-day hero-search__calendar-day--empty"></span>`
          const iso = this.toIsoDate(day)
          const disabled = iso < minDate
          const classes = [
            "hero-search__calendar-day",
            iso === todayIso ? "hero-search__calendar-day--today" : "",
            iso === selected ? "hero-search__calendar-day--selected" : "",
            disabled ? "hero-search__calendar-day--disabled" : ""
          ].filter(Boolean).join(" ")
          return `<button type="button" class="${classes}" data-date-value="${iso}" ${disabled ? "disabled" : ""}>${day.getDate()}</button>`
        }).join("")}
      </div>
    `

    this.el.dateCalendar.querySelector('[data-calendar-action="prev"]')?.addEventListener("click", (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.calendarView = this.addMonths(this.calendarView, -1)
      this.renderCalendar()
    })

    this.el.dateCalendar.querySelector('[data-calendar-action="next"]')?.addEventListener("click", (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.calendarView = this.addMonths(this.calendarView, 1)
      this.renderCalendar()
    })

    this.el.dateCalendar.querySelectorAll(".hero-search__calendar-day[data-date-value]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.selectDate(button.dataset.dateValue)
      })
    })
  }

  selectDate(isoDate) {
    this.el.dateInput.disabled = false
    this.el.dateInput.value = isoDate
    this.el.dateLabel.textContent = this.formatDate(isoDate)
    if (this.el.dateClear) this.el.dateClear.classList.remove("hidden")
    this.calendarView = this.startOfMonth(new Date(`${isoDate}T12:00:00`))
    this.closeDateDropdown()
    this.updateCompactLabel()
  }

  clearDate(event) {
    event.preventDefault()
    event.stopPropagation()
    this.setAnyDate()
    this.updateCompactLabel()
  }

  clearTime(event) {
    event.preventDefault()
    event.stopPropagation()
    this.setAnyTime()
    this.updateCompactLabel()
  }

  setAnyDate() {
    this.el.dateInput.value = ""
    this.el.dateInput.disabled = true
    this.el.dateLabel.textContent = this.anyDateValue
    if (this.el.dateClear) this.el.dateClear.classList.add("hidden")
  }

  setAnyTime() {
    if (!this.el.timeInput) return
    this.el.timeInput.value = ""
    this.el.timeInput.disabled = true
    if (this.el.timeLabel) this.el.timeLabel.textContent = this.anyTimeValue
    if (this.el.timeClear) this.el.timeClear.classList.add("hidden")
  }

  renderTimeDropdown() {
    if (!this.el.timeQuick || !this.el.timeGrid) return

    const selected = this.el.timeInput.value
    const presets = [
      { label: this.anyTimeValue, value: null },
      { label: "07:00", value: "07:00" },
      { label: "12:00", value: "12:00" },
      { label: "18:00", value: "18:00" }
    ]

    this.el.timeQuick.innerHTML = presets.map((option) => {
      const active = option.value === null ? !selected : selected === option.value
      const label = option.value ? formatDisplayTime(option.value) : option.label
      return `<button type="button" class="hero-search__date-quick-btn${active ? " hero-search__date-quick-btn--active" : ""}" data-time-value="${option.value ?? ""}">${label}</button>`
    }).join("")

    this.el.timeQuick.querySelectorAll(".hero-search__date-quick-btn").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault()
        event.stopPropagation()
        const value = button.dataset.timeValue
        if (value) {
          this.selectTime(value)
        } else {
          this.setAnyTime()
          this.closeTimeDropdown()
          this.updateCompactLabel()
        }
      })
    })

    const slots = []
    for (let hour = 0; hour <= 23; hour += 1) {
      slots.push(`${String(hour).padStart(2, "0")}:00`)
      slots.push(`${String(hour).padStart(2, "0")}:30`)
    }

    this.el.timeGrid.innerHTML = slots.map((slot) => {
      const active = selected === slot
      return `<button type="button" class="browse-schedule-picker__time-slot${active ? " browse-schedule-picker__time-slot--active" : ""}" data-time-value="${slot}">${formatDisplayTime(slot)}</button>`
    }).join("")

    this.el.timeGrid.querySelectorAll(".browse-schedule-picker__time-slot").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault()
        event.stopPropagation()
        this.selectTime(button.dataset.timeValue)
      })
    })
  }

  selectTime(value) {
    if (!this.el.timeInput) return
    this.el.timeInput.disabled = false
    this.el.timeInput.value = value
    if (this.el.timeLabel) this.el.timeLabel.textContent = formatDisplayTime(value)
    if (this.el.timeClear) this.el.timeClear.classList.remove("hidden")
    this.closeTimeDropdown()
    this.updateCompactLabel()
  }

  updateCompactLabel() {
    if (!this.el.compactLabel) return

    const location = this.el.locationInput.value.trim()
    const specializationInput = this.element.querySelector('[data-specialization-picker-target="input"]')
    const specializationLabel = this.element.querySelector('[data-specialization-picker-target="label"]')
    const hasSpec = specializationInput?.value
    const specText = hasSpec ? specializationLabel?.textContent?.trim() : null
    const hasDate = this.el.dateInput.value
    const dateText = hasDate ? this.el.dateLabel.textContent : null
    const hasTime = this.el.timeInput?.value
    const timeText = hasTime ? this.el.timeLabel.textContent : null

    const parts = [location, specText, dateText, timeText].filter(Boolean)
    if (parts.length > 0) {
      this.el.compactLabel.textContent = parts.join(" · ")
    } else {
      this.el.compactLabel.textContent = this.placeholderValue
    }
  }

  formatDate(isoDate) {
    try {
      const date = new Date(`${isoDate}T12:00:00`)
      return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
    } catch {
      return isoDate
    }
  }

  submit() {
    if (!this.el.dateInput.value) {
      this.el.dateInput.disabled = true
    }
    if (this.el.timeInput && !this.el.timeInput.value) {
      this.el.timeInput.disabled = true
    }
    const specializationInput = this.element.querySelector('[data-specialization-picker-target="input"]')
    if (specializationInput && !specializationInput.value) {
      specializationInput.disabled = true
    }
    this.closeAllDropdowns()
    this.updateCompactLabel()
  }

  startOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1)
  }

  addDays(date, amount) {
    const next = new Date(date)
    next.setDate(next.getDate() + amount)
    return next
  }

  addMonths(date, amount) {
    return new Date(date.getFullYear(), date.getMonth() + amount, 1)
  }

  toIsoDate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  weekdayLabels() {
    const base = new Date(2024, 0, 1)
    return Array.from({ length: 7 }, (_, index) => {
      const day = new Date(base)
      day.setDate(base.getDate() + index)
      return day.toLocaleDateString(undefined, { weekday: "short" })
    })
  }

  calendarDays(year, month) {
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const startOffset = (firstDay.getDay() + 6) % 7
    const days = []

    for (let i = 0; i < startOffset; i += 1) days.push(null)

    for (let day = 1; day <= lastDay.getDate(); day += 1) {
      days.push(new Date(year, month, day))
    }

    return days
  }
}